/**
 * Keeping a `position: fixed` popover inside the window.
 *
 * Every JS-positioned popover in the app portals to `<body>` — it has to, because the surfaces it
 * opens from (`FloatingChat`, a section card that rounds its rows) are `overflow: hidden` and would
 * clip it whatever its z-index. The cost of escaping that clip is that **nothing** keeps the popover
 * on screen afterwards: a rect measured off the trigger is used verbatim, and a trigger near a window
 * edge puts the panel over it.
 *
 * That is not a hypothetical. The chat docks to the right in a 370px column, so a panel hung off a
 * trigger's *left* edge inside it starts within a panel-width of the window's right edge and simply
 * runs off (Monday 12714162215), and a 3-dots trigger low in the window drops its menu past the
 * bottom — unreachable, because scrolling to it closes it.
 *
 * So the rule is the same for every popover, and lives here once rather than being re-derived (and
 * half-applied) per component: **clamp the inline axis always, flip the block axis when the preferred
 * side has no room and the other side has more.**
 */

export interface Viewport {
  width: number;
  height: number;
}

/**
 * The popover's leading edge, clamped into the window.
 *
 * `preferred` is where the alignment wants it (the trigger's start edge, centred on the trigger, or
 * its end edge minus the width). The result keeps the whole `width` between `edge` and the window's
 * far side — and when the window is too narrow to hold both margins, `edge` from the start wins, so a
 * clipped popover is clipped at its tail rather than at the text people read first.
 */
export function clampInlineStart(preferred: number, width: number, viewport: Viewport, edge: number): number {
  return Math.max(edge, Math.min(preferred, viewport.width - width - edge));
}

/**
 * The same clamp expressed as a distance from the window's END edge, for a popover that is pinned by
 * its trailing edge so it stays glued to a right-aligned trigger as things resize.
 *
 * `preferred` is `viewport.width - triggerRight`. `width` is the popover's minimum width: the clamp
 * has to reserve *something*, and the min is the only width known before the popover has rendered.
 */
export function clampInlineEnd(preferred: number, width: number, viewport: Viewport, edge: number): number {
  return Math.min(Math.max(preferred, edge), viewport.width - width - edge);
}

/** An inline-axis extent in window coordinates — e.g. the panel a popover should centre over. */
export interface InlineSpan {
  start: number;
  end: number;
}

/**
 * The centre-x for a popover centred over `span` (e.g. the docked call panel), clamped so its full
 * `width` stays inside both the span and the window (margin `edge` on each side).
 *
 * Returned as a CENTRE, so the caller positions with `left: center; transform: translateX(-50%)` —
 * which is direction-agnostic, unlike a `right` / `insetInlineEnd` anchor that flips towards empty
 * space under RTL and runs the panel off the opposite window edge. Centring over the SPAN rather than
 * the window matters when the span is a right-docked column narrower than the viewport: centring on
 * the window would strand the popover mid-screen instead of over its panel. When the span is too
 * narrow to hold the popover plus both margins, it is centred on the span anyway (clipping, symmetric,
 * is the least-bad outcome that pure geometry can give).
 */
export function centerInline(span: InlineSpan, width: number, viewport: Viewport, edge: number): number {
  const half = width / 2;
  const minCenter = Math.max(span.start, edge) + half;
  const maxCenter = Math.min(span.end, viewport.width - edge) - half;
  const center = (span.start + span.end) / 2;
  if (maxCenter < minCenter) return center;
  return Math.min(Math.max(center, minCenter), maxCenter);
}

export interface BlockPlacement {
  /** Distance from the window's top, when the popover opens downward. */
  top?: number;
  /** Distance from the window's bottom, when it opens upward. */
  bottom?: number;
}

/** The trigger's block-axis extent in window coordinates. */
export interface TriggerBlock {
  top: number;
  bottom: number;
}

/**
 * Which side of the trigger the popover opens on, and where.
 *
 * Prefers `below`, flips `above` when the popover does not fit below and there is more room above —
 * and, when it fits on **neither** side, falls back to shifting it up off the bottom edge rather than
 * leaving it to overflow. That last case is the one a pure flip cannot serve: in a short window (a
 * half-height browser, a 6-item menu in a verbose locale) both gaps are smaller than the popover, so
 * flipping only moves the clipped end from the bottom of the window to the top — and clipping the top
 * is strictly worse, because the items nearest the trigger are the ones that disappear. Shifting keeps
 * the popover whole and on screen; it merely stops being anchored to the trigger, which is the right
 * trade when the alternative is unreachable content (scrolling CLOSES these popovers).
 *
 * `height` is the popover's measured height, so this runs on a second pass once it has rendered. Pass
 * `undefined` before then and it keeps the preferred side.
 */
export function placeBlock(trigger: TriggerBlock, height: number | undefined, viewport: Viewport, gap: number, edge: number): BlockPlacement {
  const below = trigger.bottom + gap;
  if (height === undefined) return { top: below };
  const roomBelow = viewport.height - trigger.bottom - gap - edge;
  const roomAbove = trigger.top - gap - edge;
  if (height <= roomBelow) return { top: below };
  if (height <= roomAbove) return { bottom: viewport.height - trigger.top + gap };
  // Fits neither side: sit as low as the window allows, clamped so the head stays visible. A popover
  // taller than the whole window is then clipped at its TAIL, which is the survivable end.
  return { top: Math.max(edge, viewport.height - edge - height) };
}

/** The window, as these functions want it. Separated so they stay pure and testable. */
export function windowViewport(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight };
}
